import { useEffect, useMemo, useState } from "react";

import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";

import { buildUniformFilterObject } from "../utils/filterEngine";
import { commitStagedFilters } from "../utils/helpers";
import {
    filtersToUniformFilterObject,
    readStateFromUrl,
    uniformFilterObjectToFlat,
    writeStateToUrl
} from "../utils/urlSync";

import { useFireFilterClickAnalytics } from "./use-fire-filter-click-analytics";

import type { UniformFilterObject } from "../utils/types";

type DropdownVisibility = Record<string, boolean>;

interface FilterStateOptions {
    readonly items: unknown[];
    readonly filterDefs: FilterDefinition[];
    readonly urlSync: boolean;
    readonly optionsDataSources?: Record<string, unknown[]>;
}

/**
 * Owns all transient filter/search/sort UI state and the handlers that mutate
 * it. Optionally hydrates from + persists to the URL query string.
 *
 * Returns:
 * - State (`filterObject`, `searchQuery`, `selectedSortId`, `dropdownVisibility`)
 * - Derived flags (`isAnyDropdownOpen`, `closedDropdownState`)
 * - Handlers for every UI affordance
 */
export const useFilterState = ({
    items,
    filterDefs,
    urlSync,
    optionsDataSources
}: FilterStateOptions) => {
    const fireFilterClickAnalytics = useFireFilterClickAnalytics();

    const [filterObject, setFilterObject] = useState<UniformFilterObject>(() =>
        buildUniformFilterObject(items, filterDefs, optionsDataSources)
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSortId, setSelectedSortId] = useState<string | undefined>();
    const [dropdownVisibility, setDropdownVisibility] =
        useState<DropdownVisibility>({});

    const closedDropdownState = useMemo<DropdownVisibility>(
        () => Object.fromEntries(filterDefs.map((def) => [def.id, false])),
        [filterDefs]
    );

    // Reset state whenever the filter schema or data shape changes.
    // Note: closedDropdownState is intentionally excluded — it derives from filterDefs
    // so React batches any filterDefs change with the closedDropdownState recalculation.
    // Including it would add a redundant dep that fires on every filterDefs change.
    useEffect(() => {
        setFilterObject(
            buildUniformFilterObject(items, filterDefs, optionsDataSources)
        );
        setDropdownVisibility(
            Object.fromEntries(filterDefs.map((def) => [def.id, false]))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, filterDefs, optionsDataSources]);

    // Hydrate from URL on mount + on browser back/forward.
    useEffect(() => {
        if (!urlSync || typeof window === "undefined") return;

        const hydrate = () => {
            const urlState = readStateFromUrl(
                window.location.search,
                filterDefs
            );
            setFilterObject((prev) =>
                filtersToUniformFilterObject(urlState.filters, prev)
            );
            setSearchQuery(urlState.query);
            setSelectedSortId(urlState.sortId);
        };

        hydrate();
        window.addEventListener("popstate", hydrate);
        return () => window.removeEventListener("popstate", hydrate);
    }, [urlSync, filterDefs]);

    const isAnyDropdownOpen = Object.values(dropdownVisibility).some(Boolean);

    const pushUrl = (
        nextFilterObject: UniformFilterObject,
        nextQuery: string,
        nextSortId: string | undefined
    ) => {
        if (!urlSync) return;
        writeStateToUrl({
            filters: uniformFilterObjectToFlat(nextFilterObject),
            query: nextQuery,
            sortId: nextSortId
        });
    };

    const commitAndPersistFilters = (next: UniformFilterObject) => {
        const committed = commitStagedFilters(next);
        setFilterObject(committed);
        pushUrl(committed, searchQuery, selectedSortId);
    };

    const handleFilterButtonClick = (filterId: string) => {
        fireFilterClickAnalytics(filterId);
        const wasOpen = !!dropdownVisibility[filterId];
        setDropdownVisibility((prev) => ({
            ...closedDropdownState,
            [filterId]: !prev[filterId]
        }));
        if (wasOpen) commitAndPersistFilters(filterObject);
    };

    const toggleFilterOption = (filterId: string, optionId: string) => {
        setFilterObject((prev) => {
            const subFilter = prev[filterId] ?? {};
            const current = subFilter[optionId];
            return {
                ...prev,
                [filterId]: {
                    ...subFilter,
                    [optionId]: {
                        committedActive: current?.committedActive ?? false,
                        stagedActive: !current?.stagedActive
                    }
                }
            };
        });
    };

    const clearAllFilters = () => {
        const cleared = mapAllOptionsToInactive(filterObject);
        setFilterObject(cleared);
        pushUrl(cleared, searchQuery, selectedSortId);
    };

    const handleSearchSubmit = () =>
        pushUrl(filterObject, searchQuery, selectedSortId);

    const handleSearchClear = () => {
        setSearchQuery("");
        pushUrl(filterObject, "", selectedSortId);
    };

    const handleSortChange = (nextSortId: string) => {
        setSelectedSortId(nextSortId);
        pushUrl(filterObject, searchQuery, nextSortId);
    };

    const closeAllDropdownsAndCommit = () => {
        setDropdownVisibility(closedDropdownState);
        commitAndPersistFilters(filterObject);
    };

    return {
        filterObject,
        searchQuery,
        selectedSortId,
        dropdownVisibility,
        isAnyDropdownOpen,
        setSearchQuery,
        handleFilterButtonClick,
        toggleFilterOption,
        clearAllFilters,
        handleSearchSubmit,
        handleSearchClear,
        handleSortChange,
        closeAllDropdownsAndCommit
    };
};

const mapAllOptionsToInactive = (
    state: UniformFilterObject
): UniformFilterObject =>
    Object.fromEntries(
        Object.entries(state).map(([filterId, subFilter]) => [
            filterId,
            Object.fromEntries(
                Object.keys(subFilter ?? {}).map((optionId) => [
                    optionId,
                    { committedActive: false, stagedActive: false }
                ])
            )
        ])
    );
